import { Link } from "react-router-dom";
import { X, Radio as RadioIcon } from "lucide-react";
import { useRadioPlayer, spotifyEmbedUrl } from "@/lib/radioPlayer";

/**
 * Player fixo no rodapé da aplicação. Fica oculto até o usuário clicar
 * "Tocar" em algum canal ou episódio da Rádio JurisTube. Uma vez
 * ativo, mantém o iframe do Spotify montado em todas as rotas — o
 * usuário pode navegar livremente sem interromper a reprodução.
 *
 * O Spotify embed compact tem altura ideal de 152px (controles
 * + capa + título + barra de progresso) sem ocupar muito espaço
 * vertical, então usamos 160px com leve padding interno.
 */
export default function StickyRadioPlayer() {
  const { current, stop } = useRadioPlayer();

  if (!current) return null;

  const src = spotifyEmbedUrl(current.kind, current.spotifyId, current.seekTime);
  if (!src) return null;

  const title =
    current.kind === "show"
      ? current.channelName || "Canal de Rádio"
      : current.episodeTitle || "Episódio";
  const subtitle =
    current.kind === "show"
      ? "Reprodução contínua do show"
      : current.channelName
      ? `Canal · ${current.channelName}`
      : "Episódio único";
  const pageHref =
    current.kind === "show"
      ? `/radio/${current.channelSlug}`
      : `/radio/${current.channelSlug}/${current.episodeSlug}`;

  return (
    <div
      data-testid="sticky-radio-player"
      role="region"
      aria-label="Player da Rádio JurisTube"
      className="fixed bottom-0 left-0 right-0 z-[60] border-t border-amber-500/30 bg-slate-950/95 backdrop-blur supports-[backdrop-filter]:bg-slate-950/85 shadow-[0_-8px_30px_rgba(0,0,0,0.5)]"
    >
      <div className="mx-auto max-w-7xl px-3 sm:px-6 py-2.5 flex items-center gap-3">
        {/* Identificador esquerdo — eyebrow âmbar */}
        <div className="hidden md:flex flex-col shrink-0 w-44 lg:w-56">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-400">
            <RadioIcon className="w-3.5 h-3.5" aria-hidden="true" />
            Rádio JurisTube
          </span>
          <Link
            to={pageHref}
            className="text-sm font-semibold text-white truncate hover:text-amber-300 transition"
            data-testid="sticky-radio-player-title-link"
            title={title}
          >
            {title}
          </Link>
          <span className="text-[10px] text-slate-400 truncate">{subtitle}</span>
        </div>

        {/* iframe central — o player propriamente dito */}
        <div className="flex-1 min-w-0">
          <iframe
            key={`${src}#${current.seekNonce || 0}`}
            src={src}
            title={`Player do Spotify — ${title}`}
            width="100%"
            height="80"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            loading="lazy"
            data-testid="sticky-radio-player-iframe"
            className="rounded-md"
          />
        </div>

        {/* Botão fechar */}
        <button
          type="button"
          onClick={stop}
          aria-label="Fechar player da Rádio"
          data-testid="sticky-radio-player-close"
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-amber-400 transition"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
